<template>
  <div class="d-flex flex-v-center gap-small w-100">
    <!-- Превью позиции -->
    <div class="w-[3.25rem] h-[3.25rem] flex-child-none o-hidden radius-small bg-card">
      <img
        v-if="images && images[0]"
        loading="lazy"
        class="w-100 h-100 object-fit-cover"
        :src="(FILE_SERVER_URL || '') + images[0]"
      >
      <PlaceholderImage v-else class="w-100 h-100" />
    </div>

    <!-- Название + цена -->
    <div class="flex-child-1 mn-w-0">
      <p class="truncate fw-medium">{{ name }}</p>

      <div class="mn-t-thin">
        <p v-if="price != null && quantity != null" class="d-flex flex-v-center flex-wrap gap-thin">
          <span class="fw-semi">{{ formatPrice({ amount: totalPrice, currency }) }}</span>
          <span v-if="isRental && rentDates.start && rentDates.end" class="p-small t-muted">
            {{ formatPrice({ amount: price, currency }) }}
            <span v-if="quantity > 1">× {{ quantity }} {{ unitLabel }}</span>
            × {{ rentalDurationLabel }}
          </span>
          <!-- Разбивка цены только когда она добавляет информацию: при qty=1 total = цена -->
          <span v-else-if="(quantity || 0) > 1" class="p-small t-muted">{{ formatPrice({ amount: price, currency }) }} × {{ quantity }} {{ unitLabel }}</span>
        </p>

        <p v-else-if="price != null && quantity == null" class="fw-semi">
          {{ formatPrice({ amount: price, currency }) }}
        </p>

        <p v-else-if="price == null && quantity != null" class="p-small t-muted">
          {{ quantity }} {{ unitLabel }}
        </p>
      </div>

      <slot></slot>

      <div v-if="showControls" class="mn-t-small d-flex flex-wrap gap-thin">
        <div
          v-if="isRental && rentDates.start && rentDates.end"
          @click="editable ? openRentDatePopup() : null"
          :class="['d-flex gap-thin flex-v-center pd-thin pd-l-small pd-r-small w-100 bg-card radius-small', editable ? 'cursor-pointer' : '']"
        >
          <IconCalendar class="i-medium" />
          <p class="fw-medium t-muted">{{ rentDateLabel }}</p>
        </div>

        <template v-if="editable && !isRental">
          <IconAdd @click="increase" class="bg-card hover:bg-card-transp-10 i-big pd-thin radius-small cursor-pointer" />
          <IconMinus @click="decrease" class="bg-card hover:bg-card-transp-10 i-big pd-thin radius-small cursor-pointer" />
          <IconDelete @click="remove" class="bg-card hover:bg-card-transp-10 i-big pd-thin radius-small cursor-pointer" />
        </template>

        <IconEdit
          v-if="isRental && editable"
          @click="openRentDatePopup"
          class="bg-card hover:bg-card-transp-10 i-big pd-small radius-small cursor-pointer"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import IconAdd from '@martyrs/src/modules/icons/navigation/IconAdd.vue'
import IconEdit from '@martyrs/src/modules/icons/navigation/IconEdit.vue'
import IconMinus from '@martyrs/src/modules/icons/navigation/IconMinus.vue'
import IconDelete from '@martyrs/src/modules/icons/navigation/IconDelete.vue'
import IconCalendar from '@martyrs/src/modules/icons/entities/IconCalendar.vue'
import PlaceholderImage from '@martyrs/src/modules/icons/placeholders/PlaceholderImage.vue'
import { useGlobalMixins } from '@martyrs/src/modules/core/views/mixins/mixins.js'
import { calcRentTotalPrice, formatRentDuration, formatRentDateLabel } from '@martyrs/src/modules/orders/utils/rent-price.js'

const router = useRouter()
const { locale, t, te } = useI18n()
const { proxy } = getCurrentInstance()
const { formatPrice, formatDate } = useGlobalMixins()

// Локализованная единица измерения (pcs/day/month…); неизвестную — как есть.
const unitLabel = computed(() => {
  const u = props.unit || 'pcs'
  return te(`orders.units.${u}`) ? t(`orders.units.${u}`) : u
})

const emits = defineEmits(['increase', 'decrease', 'remove', 'updateRentDates'])
const props = defineProps({
  productId: String,
  variantId: String,
  images: Array,
  name: String,
  quantity: Number,
  unit: String,
  dates: Object,
  listing: String,
  // Сумма позиции в минорных единицах + валюта заказа: позиция сама по себе
  // валюты не знает, её задаёт заказ.
  price: Number,
  currency: String,
  editable: Boolean,
  increase: Function,
  decrease: Function,
  remove: Function,
})

const isRental = computed(() => props.listing === 'rent')
const showControls = computed(() => props.editable || isRental.value)

const rentDates = ref({
  start: props.dates?.start || null,
  end: props.dates?.end || null
})

const isShortPeriod = computed(() => ['hour', '4hours', 'flat'].includes(props.unit))

const rentalDurationLabel = computed(() => {
  if (!rentDates.value.start || !rentDates.value.end) return ''
  return formatRentDuration(rentDates.value.start, rentDates.value.end, props.unit)
})

const rentDateLabel = computed(() => {
  return formatRentDateLabel(rentDates.value.start, rentDates.value.end, props.unit, formatDate)
})

const totalPrice = computed(() => {
  if (isRental.value && rentDates.value.start && rentDates.value.end) {
    return calcRentTotalPrice(props.price, props.quantity, rentDates.value.start, rentDates.value.end, props.unit)
  }
  return props.quantity * props.price
})

const isRentDatePopupOpen = ref(false)

function increase() {
  props.increase?.()
  emits('increase')
}

function decrease() {
  props.decrease?.()
  emits('decrease')
}

function remove() {
  props.remove?.()
  emits('remove')
}

async function openRentDatePopup() {
  try {
    const selectedDates = await proxy.$dateSelector(
      props.productId,
      props.variantId,
      props.quantity,
      props.price,
      {
        showCancelButton: props.editable,
        onCancel: props.editable ? () => props.remove?.() : null,
        enableTime: isShortPeriod.value,
        periodType: props.unit,
        initialDates: rentDates.value.start ? { start: rentDates.value.start, end: rentDates.value.end } : null
      }
    );

    if (selectedDates) {
      updateRentDates(selectedDates)
    }
  } catch (error) {
    console.error('Error selecting dates:', error)
  }
}

function updateRentDates(dates) {
  rentDates.value = dates
  emits('updateRentDates', props.productId, props.variantId, dates)
}
</script>
